import { standardSchemaResolver } from "@hookform/resolvers/standard-schema";
import { type UseFormProps, useForm } from "react-hook-form";
import * as z from "zod/mini";

z.config(z.locales.en());
z.config({
  customError: (issue) => {
    if (issue.code === "invalid_type" && issue.input == null) {
      return "This field is required";
    }
  },
});

const requiredStringSchema = z.string().check(z.trim(), z.minLength(1, "This field is required"));

const usPhoneNumberRegex = /^(?:\+?1)?(?:\([2-9]\d{2}\)|[2-9]\d{2})[2-9]\d{6}$/;
const isUsPhoneNumber = (value: string) => usPhoneNumberRegex.test(value.replaceAll(/[-. ]/g, ""));
const invalidPhoneNumber = "Invalid phone number";
const phoneNumberSchema = requiredStringSchema.check(z.refine(isUsPhoneNumber, invalidPhoneNumber));
const optionalPhoneNumberSchema = z.string().check(
  z.trim(),
  z.refine((value) => !value || isUsPhoneNumber(value), invalidPhoneNumber),
);

const postalCodeRegex = /^\d{5}(?:[-\s]\d{4})?$/;
const postalCodeSchema = requiredStringSchema.check(z.regex(postalCodeRegex, "Invalid postal code"));

type UseZodFormProps<TSchema extends z.core.$ZodType<any, any>, TContext> = Pick<
  UseFormProps<TSchema["_zod"]["input"], TContext>,
  "defaultValues" | "values" | "resetOptions" | "context"
> & {
  schema: TSchema;
  schemaTransform?: (
    schema: TSchema,
    values: TSchema["_zod"]["input"],
    context: TContext | undefined,
  ) => Promise<z.core.$ZodType<TSchema["_zod"]["output"], any>> | z.core.$ZodType<TSchema["_zod"]["output"], any>;
};
const useZodForm = <TSchema extends z.core.$ZodType<any, any>, TContext = any>({
  schema,
  schemaTransform,
  ...props
}: UseZodFormProps<TSchema, TContext>) =>
  useForm<TSchema["_zod"]["output"], TContext, TSchema["_zod"]["output"]>({
    mode: "onChange",
    resolver: schemaTransform
      ? async (data, context, options) =>
          standardSchemaResolver(await schemaTransform(schema, data, context))(data, context, options)
      : standardSchemaResolver(schema),
    delayError: 500,
    ...props,
  });

const removeEmptyFields = <TData extends Record<string, any> = Record<string, any>>(data: TData) =>
  Object.fromEntries(Object.entries(data).filter(([, value]) => value !== "" && value != null)) as TData;

export {
  optionalPhoneNumberSchema,
  phoneNumberSchema,
  postalCodeRegex,
  postalCodeSchema,
  removeEmptyFields,
  requiredStringSchema,
  useZodForm,
};
